import Image from 'next/image';

import { SectionWrapper } from '@/components/atomic/nuclei';
import { AGENTS } from '@/config/landingPage';

const AgentCard = ({
  data: { description, name, photo, role },
  className = ''
}) => {
  return (
    <div
      className={`relative bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg pt-2 p-5 sm:pt-3 sm:p-6 md:pt-3 md:p-7 lg:pt-4 lg:p-8 flex flex-col border border-pink-100 hover:border-pink-200 hover:shadow-xl transition ease-in-out ${className}`}
    >
      <div className='absolute xs:-top-4 sm:-top-12 md:-top-16 left-6 xs:w-16 xs:h-16 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-36 lg:h-36'>
        <div className='w-full h-full rounded-full overflow-hidden shadow-md'>
          <Image
            alt={name}
            className='object-cover w-full h-full'
            height={144}
            priority
            sizes='(max-width: 640px) 96px, (max-width: 768px) 112px, 144px'
            src={photo}
            width={144}
          />
        </div>
      </div>
      <div
        className={`ml-28 xs:ml-20 sm:ml-36 md:ml-40 lg:ml-40 xl:ml-40 flex flex-col`}
      >
        <div className='text-lg xs:text-base sm:text-base md:text-2xl lg:text-3xl font-extrabold text-gray-900 leading-tight'>
          {name}
        </div>
        <div className='text-xs xs:text-xs sm:text-sm md:text-base text-gray-600 mt-0.5 sm:mt-1'>
          {role}
        </div>
      </div>
      <div className='mt-3 xs:mt-3.5 sm:mt-4 text-xs xs:text-xs sm:text-sm md:text-base text-gray-700 leading-relaxed'>
        {description}
      </div>
    </div>
  );
};

const BubbleBackground = () => (
  <div className='absolute inset-0 overflow-hidden rounded-[16px] xs:rounded-[18px] sm:rounded-[24px] md:rounded-[28px]'>
    <div
      className={`pointer-events-none absolute -top-8 xs:-top-10 sm:-top-12 -left-8 xs:-left-10 sm:-left-12 h-40 xs:h-48 sm:h-56 w-40 xs:w-48 sm:w-56 rounded-full bg-white/20`}
    />
    <div
      className={`pointer-events-none absolute top-4 xs:top-5 sm:top-6 left-1/2 h-16 xs:h-20 sm:h-24 w-16 xs:w-20 sm:w-24 -translate-x-1/2 rounded-full bg-white/10`}
    />
    <div
      className={`pointer-events-none absolute -bottom-8 xs:-bottom-10 sm:-bottom-12 right-4 xs:right-6 sm:right-8 h-64 xs:h-72 sm:h-80 w-64 xs:w-72 sm:w-80 rounded-full bg-white/15`}
    />
    <div
      className={`pointer-events-none absolute bottom-4 xs:bottom-5 sm:bottom-6 right-16 xs:right-20 sm:right-24 h-16 xs:h-20 sm:h-24 w-16 xs:w-20 sm:w-24 rounded-full bg-white/20`}
    />
  </div>
);

const IntelligentForce = () => {
  const cardLayoutClasses = [
    'flex overflow-x-auto snap-x snap-mandatory',
    'px-1 pb-2 gap-4 sm:gap-6',
    '[-ms-overflow-style:none] [scrollbar-width:none]',
    '[&::-webkit-scrollbar]:hidden'
  ].join(' ');

  return (
    <SectionWrapper className='relative'>
      <div
        className={`relative rounded-[16px] xs:rounded-[18px] xs:rounded-br-[80px] sm:rounded-[20px] md:rounded-[24px] rounded-br-[80px] sm:rounded-br-[100px] md:rounded-br-[120px] px-4 xs:px-5 sm:px-6 md:px-8 lg:px-12 xl:px-16 pt-8 xs:pt-9 sm:pt-10 md:pt-12 lg:pt-14 xl:pt-16 pb-24 xs:pb-28 sm:pb-32 md:pb-40 lg:pb-52 bg-brand-gradient text-white`}
      >
        <BubbleBackground />
        <div className='relative max-w-4xl xs:max-w-5xl mx-auto text-center px-2 xs:px-4'>
          <div
            className={`text-2xl xs:text-2xl sm:text-3xl md:text-[40px] font-extrabold leading-tight md:text-stroke-normal`}
          >
            The Intelligent Force Behind Every Event
          </div>
          <div
            className={`mt-2 xs:mt-3 sm:mt-4 text-xs xs:text-sm sm:text-base md:text-lg lg:text-xl text-white mx-auto leading-relaxed`}
          >
            Each AI agent is designed to streamline your journey — from vision
            to execution — seamlessly synchronized with human expertise.
          </div>
          <div className='flex justify-center mt-3 sm:mt-4 gap-2'>
            <div className='h-1 w-16 sm:w-20 bg-white rounded-full' />
            <div className='h-1 w-4 sm:w-5 bg-white rounded-full' />
          </div>
        </div>
      </div>

      <div
        className={`relative mt-[-4rem] xs:mt-[-7rem] sm:mt-[-8rem] md:mt-[-11rem] pb-10 sm:pb-12 md:pb-16 lg:pb-20 -mx-4 sm:-mx-6 md:-mx-8 lg:-mx-12 xl:-mx-16`}
      >
        <div
          className={[
            `px-3 xs:px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 xs:max-w-[18rem] sm:max-w-xl md:max-w-2xl lg:max-w-[60rem] xl:max-w-6xl 2xl:max-w-[90rem] mx-auto`,
            'pt-10 xs:pt-12 sm:pt-14 md:pt-16 lg:pt-20',
            cardLayoutClasses
          ].join(' ')}
        >
          {AGENTS.map((agent) => (
            <AgentCard
              className='snap-start min-w-[85%] xs:min-w-[90%] sm:min-w-[85%] md:min-w-[90%] lg:min-w-[30%] xl:min-w-[25%] 2xl:min-w-[22%]'
              data={agent}
              key={agent.id}
            />
          ))}
        </div>
      </div>
    </SectionWrapper>
  );
};

export default IntelligentForce;
